/**
 * A passage a document should open at (RFC 0324 3f).
 *
 * The holdings count opens a document at the thread waiting on you, but the
 * pane that will show it does not exist yet when the count is clicked, and its
 * view commands (RFC 0328) register later still, once the editor has mounted.
 * So the click leaves the passage here, keyed by document, and the pane takes
 * it when its commands arrive. Taking it clears it: a passage is a one-time
 * request, not a sticky preference, and reopening the document later must not
 * jump back to it.
 */

const pending = new Map<string, string>()
const listeners = new Set<(documentId: string) => void>()

export function setPendingPassage(
  documentId: string,
  anchorText: string,
): void {
  pending.set(documentId, anchorText)
  // A pane already showing this document will not mount again, so it has to
  // be told rather than find out on mount.
  for (const fn of listeners) fn(documentId)
}

/** Hear about a passage requested for any document. Returns the unsubscribe. */
export function onPendingPassage(fn: (documentId: string) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function takePendingPassage(documentId: string): string | undefined {
  const anchor = pending.get(documentId)
  pending.delete(documentId)
  return anchor
}

/** Whether a document has a passage waiting, without taking it. */
export function hasPendingPassage(documentId: string): boolean {
  return pending.has(documentId)
}
